import React from "react";
import PropTypes from "prop-types";
import { Row, Col } from "reactstrap";
import { useFormik } from "formik";
import { memberSchema, initialMemberValues } from "../../../models/member";
import { 
    FormPhotoUpload, 
    StatusBadge,
    NeoModal,
    NeoTabs,
    NeoInput,
    NeoButton
} from "../../../components/Common";
import { useCEP } from "../../../hooks/useCEP";
import { GENDER_OPTIONS } from "../../../constants/gender";

const convertISOToBrDate = (isoDate) => {
    if (!isoDate) return "";
    const parts = isoDate.split("-");
    if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return isoDate;
};

const convertBrDateToISO = (brDate) => {
    if (!brDate) return "";
    const parts = brDate.split("/");
    if (parts.length === 3) {
        const day = parts[0];
        const month = parts[1];
        const year = parts[2];
        if (day.length === 2 && month.length === 2 && year.length === 4) {
            return `${year}-${month}-${day}`;
        }
    }
    return "invalid";
};

const MemberFormModal = ({ isOpen, toggle, onSave, data, isSaving }) => {
    const isEdit = !!data?.id;
    const [activeTab, setActiveTab] = React.useState("1");
    const { searchCEP, loading: loadingCEP } = useCEP();
    const [birthDateInput, setBirthDateInput] = React.useState("");

    const formik = useFormik({
        initialValues: initialMemberValues,
        validationSchema: memberSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave(values);
        },
    });

    React.useEffect(() => {
        if (isOpen && data) {
            // Os dados já vêm normalizados do MemberService (via memberDataFromFirestore)
            formik.setValues({
                ...initialMemberValues,
                ...data,
                emergencyContact: { ...initialMemberValues.emergencyContact, ...data.emergencyContact },
                healthInfo: { ...initialMemberValues.healthInfo, ...data.healthInfo }
            });
            if (data.birthDate) {
                setBirthDateInput(convertISOToBrDate(data.birthDate));
            } else {
                setBirthDateInput("");
            }
            setActiveTab("1");
        } else if (isOpen) {
            formik.resetForm();
            setBirthDateInput("");
            setActiveTab("1");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, data]);

    const handleBirthDateChange = (e) => {
        let val = e.target.value;
        val = val.replace(/\D/g, "");
        if (val.length > 8) val = val.substring(0, 8);
        
        // Format as DD/MM/YYYY
        let formatted = val;
        if (val.length > 4) {
            formatted = `${val.substring(0, 2)}/${val.substring(2, 4)}/${val.substring(4)}`;
        } else if (val.length > 2) {
            formatted = `${val.substring(0, 2)}/${val.substring(2)}`;
        }
        
        setBirthDateInput(formatted);
        
        // Update Formik
        if (formatted.length === 10) {
            formik.setFieldValue("birthDate", convertBrDateToISO(formatted));
        } else if (formatted.length === 0) {
            formik.setFieldValue("birthDate", "");
        } else {
            formik.setFieldValue("birthDate", "invalid");
        }
    };

    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    const tabs = [
        { id: "1", label: "Dados Pessoais", icon: "mdi-account-circle-outline" },
        { id: "2", label: "Endereço", icon: "mdi-map-marker-outline" },
        { id: "3", label: "Saúde e Emergência", icon: "mdi-heart-pulse" }
    ];

    return (
        <NeoModal
            isOpen={isOpen}
            toggle={toggle}
            title={isEdit ? 'Editar Cliente' : 'Novo Cliente'}
            subtitle="Gestão completa do perfil e cadastro do membro do centro esportivo."
            headerIcon={isEdit ? 'mdi-account-edit-outline' : 'mdi-account-plus-outline'}
            size="xl"
            footerActions={
                <React.Fragment>
                    <NeoButton 
                        variant="outline-chocolate" 
                        onClick={toggle} 
                        disabled={isSaving}
                    >
                        Sair sem salvar
                    </NeoButton>
                    <NeoButton
                        variant="primary"
                        icon="mdi-check"
                        loading={isSaving}
                        onClick={formik.handleSubmit}
                    >
                        {isEdit ? 'Salvar Alterações' : 'Finalizar Cadastro'}
                    </NeoButton>
                </React.Fragment>
            }
        >
            {/* Abas Superiores Customizadas do Design System */}
            <div className="px-1 pt-1 text-center">
                <NeoTabs
                    tabs={tabs}
                    activeTab={activeTab}
                    onChangeTab={toggleTab}
                    style={{ marginBottom: '22px' }}
                />
            </div>

            {/* Conteúdo das Abas */}
            <div>
                {/* TAB 1: DADOS PESSOAIS */}
                {activeTab === "1" && (
                    <Row>
                        <Col lg={3} className="text-center mb-4">
                            <div className="p-3 bg-light rounded-4 h-100 d-flex flex-column align-items-center justify-content-center border border-dashed" style={{ borderColor: 'rgba(72, 39, 18, 0.15)' }}>
                                <label className="fw-bold text-muted small text-uppercase mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                    Foto de Perfil
                                </label>
                                <FormPhotoUpload
                                    photoPreview={formik.values.photoUrl}
                                    onChange={(url) => formik.setFieldValue("photoUrl", url)}
                                    size="lg"
                                />
                            </div>
                        </Col>
                        <Col lg={9}>
                            <div className="bg-white p-1">
                                <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-4" style={{ letterSpacing: '0.5px' }}>
                                    Informações de Identificação
                                </h6>
                                <Row className="g-3">
                                    <Col md={4}>
                                        <NeoInput
                                            label="Nome"
                                            placeholder="Ex: João"
                                            disabled={isSaving}
                                            {...formik.getFieldProps("firstName")}
                                            value={formik.values.firstName || ""}
                                            error={formik.touched.firstName ? formik.errors.firstName : ""}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <NeoInput
                                            label="Sobrenome"
                                            placeholder="Ex: Silva"
                                            disabled={isSaving}
                                            {...formik.getFieldProps("lastName")}
                                            value={formik.values.lastName || ""}
                                            error={formik.touched.lastName ? formik.errors.lastName : ""}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <NeoInput
                                            label="CPF / Documento"
                                            placeholder="000.000.000-00"
                                            disabled={isSaving}
                                            {...formik.getFieldProps("document")}
                                            value={formik.values.document || ""}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <NeoInput
                                            label="Data de Nascimento"
                                            placeholder="DD/MM/AAAA"
                                            disabled={isSaving}
                                            value={birthDateInput}
                                            onChange={handleBirthDateChange}
                                            onBlur={() => formik.setFieldTouched("birthDate", true)}
                                            error={formik.touched.birthDate ? formik.errors.birthDate : ""}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <NeoInput
                                            label="Gênero / Sexo"
                                            type="select"
                                            options={[
                                                { value: "", label: "Selecione" },
                                                ...GENDER_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))
                                            ]}
                                            disabled={isSaving}
                                            {...formik.getFieldProps("gender")}
                                        />
                                    </Col>
                                    <Col md={4} className="d-flex align-items-end pb-3.5">
                                        <div className="d-flex flex-column justify-content-end h-100">
                                            <span className="neo-input-label mb-1.5">Status Cadastral</span>
                                            <StatusBadge status={formik.values.status} size="md" />
                                        </div>
                                    </Col>

                                    <Col md={12}>
                                        <hr className="my-2" style={{ border: 0, borderTop: '1px dashed var(--color-brand-primary)', opacity: 0.18, backgroundColor: 'transparent' }} />
                                    </Col>

                                    <Col md={6}>
                                        <NeoInput
                                            label="Telefone Principal (WhatsApp)"
                                            placeholder="(00) 00000-0000"
                                            icon="mdi-whatsapp"
                                            disabled={isSaving}
                                            {...formik.getFieldProps("phone")}
                                            value={formik.values.phone || ""}
                                            error={formik.touched.phone ? formik.errors.phone : ""}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <NeoInput
                                            label="E-mail de Contato"
                                            type="email"
                                            placeholder="contato@email.com"
                                            icon="mdi-email-outline"
                                            disabled={isSaving}
                                            {...formik.getFieldProps("email")}
                                            value={formik.values.email || ""}
                                            error={formik.touched.email ? formik.errors.email : ""}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <NeoInput
                                            label="Responsável"
                                            placeholder="Nome do pai/mãe ou responsável pelo contato"
                                            icon="mdi-account-supervisor-outline"
                                            disabled={isSaving}
                                            {...formik.getFieldProps("responsibleName")}
                                            value={formik.values.responsibleName || ""}
                                            error={formik.touched.responsibleName ? formik.errors.responsibleName : ""}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </Col>
                    </Row>
                )}

                {/* TAB 2: ENDEREÇO */}
                {activeTab === "2" && (
                    <div className="bg-white p-1">
                        <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-4 d-flex align-items-center" style={{ letterSpacing: '0.5px' }}>
                            <i className="mdi mdi-map-marker-radius-outline me-2 text-primary font-size-16"></i>
                            Localização e Endereço
                        </h6>
                        <Row className="g-3">
                            <Col md={3}>
                                <NeoInput
                                    label="CEP"
                                    placeholder="00000-000"
                                    value={formik.values.zipCode || ""}
                                    onChange={async (e) => {
                                        const val = e.target.value;
                                        formik.setFieldValue("zipCode", val);
                                        const cleanVal = val.replace(/\D/g, "");
                                        if (cleanVal.length === 8) {
                                            const addr = await searchCEP(cleanVal);
                                            if (addr) {
                                                formik.setFieldValue("address", addr.street || formik.values.address);
                                                formik.setFieldValue("neighborhood", addr.neighborhood || formik.values.neighborhood);
                                                formik.setFieldValue("city", addr.city || formik.values.city);
                                                formik.setFieldValue("state", addr.state || formik.values.state);
                                            }
                                        }
                                    }}
                                    icon={loadingCEP ? "mdi-loading mdi-spin" : "mdi-magnify"}
                                    iconRight={true}
                                    disabled={isSaving}
                                />
                            </Col>
                            <Col md={3}>
                                <NeoInput
                                    label="Estado (UF)"
                                    placeholder="Ex: SP"
                                    disabled={isSaving}
                                    {...formik.getFieldProps("state")}
                                    value={formik.values.state || ""}
                                />
                            </Col>
                            <Col md={6}>
                                <NeoInput
                                    label="Cidade"
                                    placeholder="Cidade"
                                    disabled={isSaving}
                                    {...formik.getFieldProps("city")}
                                    value={formik.values.city || ""}
                                />
                            </Col>
                            <Col md={4}>
                                <NeoInput
                                    label="Bairro"
                                    placeholder="Bairro"
                                    disabled={isSaving}
                                    {...formik.getFieldProps("neighborhood")}
                                    value={formik.values.neighborhood || ""}
                                />
                            </Col>
                            <Col md={6}>
                                <NeoInput
                                    label="Logradouro"
                                    placeholder="Rua, Avenida, Praça..."
                                    disabled={isSaving}
                                    {...formik.getFieldProps("address")}
                                    value={formik.values.address || ""}
                                />
                            </Col>
                            <Col md={2}>
                                <NeoInput
                                    label="Nº"
                                    placeholder="999"
                                    disabled={isSaving}
                                    {...formik.getFieldProps("number")}
                                    value={formik.values.number || ""}
                                />
                            </Col>
                            <Col md={12}>
                                <NeoInput
                                    label="Complemento"
                                    placeholder="Apto, Bloco, Travessa..."
                                    disabled={isSaving}
                                    {...formik.getFieldProps("complement")}
                                    value={formik.values.complement || ""}
                                />
                            </Col>
                        </Row>
                    </div>
                )}

                {/* TAB 3: SAÚDE E EMERGÊNCIA */}
                {activeTab === "3" && (
                    <Row className="g-4">
                        <Col md={6}>
                            <div className="p-4 bg-light rounded-4 h-100 border border-dashed" style={{ borderColor: 'rgba(239, 68, 68, 0.25)' }}>
                                <h6 className="text-danger fw-bold text-uppercase font-size-11 mb-4 d-flex align-items-center" style={{ letterSpacing: '0.5px' }}>
                                    <i className="mdi mdi-alert-circle-outline me-2 font-size-18"></i>
                                    Contato de Emergência
                                </h6>
                                <Row className="g-3">
                                    <Col md={12}>
                                        <NeoInput
                                            label="Nome do Contato"
                                            placeholder="Nome completo"
                                            disabled={isSaving}
                                            {...formik.getFieldProps("emergencyContact.name")}
                                            value={formik.values.emergencyContact?.name || ""}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <NeoInput
                                            label="Parentesco"
                                            placeholder="Pai, Cônjuge, etc."
                                            disabled={isSaving}
                                            {...formik.getFieldProps("emergencyContact.relationship")}
                                            value={formik.values.emergencyContact?.relationship || ""}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <NeoInput
                                            label="Telefone"
                                            placeholder="(00) 00000-0000"
                                            disabled={isSaving}
                                            {...formik.getFieldProps("emergencyContact.phone")}
                                            value={formik.values.emergencyContact?.phone || ""}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="p-4 bg-light rounded-4 h-100 border border-dashed" style={{ borderColor: 'rgba(226, 102, 28, 0.25)' }}>
                                <h6 className="text-info fw-bold text-uppercase font-size-11 mb-4 d-flex align-items-center" style={{ color: 'var(--color-brand-accent) !important', letterSpacing: '0.5px' }}>
                                    <i className="mdi mdi-heart-pulse me-2 font-size-18"></i>
                                    Informações de Saúde
                                </h6>
                                <Row className="g-3">
                                    <Col md={12}>
                                        <NeoInput
                                            label="Tipo Sanguíneo"
                                            type="select"
                                            options={[
                                                { value: "", label: "Não informado" },
                                                { value: "A+", label: "A+" },
                                                { value: "A-", label: "A-" },
                                                { value: "B+", label: "B+" },
                                                { value: "B-", label: "B-" },
                                                { value: "AB+", label: "AB+" },
                                                { value: "AB-", label: "AB-" },
                                                { value: "O+", label: "O+" },
                                                { value: "O-", label: "O-" }
                                            ]}
                                            disabled={isSaving}
                                            {...formik.getFieldProps("healthInfo.bloodType")}
                                        />
                                    </Col>
                                    <Col md={12}>
                                        <NeoInput
                                            label="Observações Médicas / Alergias"
                                            type="textarea"
                                            rows="4"
                                            placeholder="Descreva condições médicas importantes ou alergias..."
                                            disabled={isSaving}
                                            {...formik.getFieldProps("healthInfo.observations")}
                                            value={formik.values.healthInfo?.observations || ""}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </Col>
                    </Row>
                )}
            </div>

            {formik.submitCount > 0 && !formik.isValid && (
                <div className="px-1 pt-3 text-center">
                    <div className="alert alert-danger py-2 mb-0 border-0 shadow-sm d-inline-flex align-items-center" style={{ borderRadius: '10px' }}>
                        <i className="mdi mdi-alert-circle-outline font-size-18 me-2"></i>
                        <span className="font-size-12 fw-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Existem campos obrigatórios incorretos. Revise as abas acima.
                        </span>
                    </div>
                </div>
            )}
        </NeoModal>
    );
};

MemberFormModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    data: PropTypes.object,
    isSaving: PropTypes.bool,
};

export default MemberFormModal;
